import { BALANCE } from './balance';
import type { PlayerId, Timestamp } from './types';

/**
 * Account vocabulary and the rules a username and an e-mail must satisfy.
 *
 * These live in `shared` so the form and the endpoint reach the same verdict.
 * A client-side rule the server does not enforce is a suggestion; a server-side
 * rule the client does not know is a round trip that fails for a reason the
 * player could have been told while typing. Both halves call these functions.
 *
 * The server still validates on every request: the client's copy is a courtesy,
 * never the authority.
 */

// --- username ----------------------------------------------------------------

export type UsernameError = 'required' | 'too_short' | 'too_long' | 'charset' | 'edges';

/**
 * Letters, digits and underscore. Deliberately no spaces, punctuation or
 * non-ASCII: this string is both the sign-in identifier and the name drawn on a
 * leaderboard row, so two names that are indistinguishable on a CRT-styled
 * screen must not be able to belong to two different people.
 */
const USERNAME_PATTERN = /^[A-Za-z0-9_]+$/;

export function usernameError(raw: unknown): UsernameError | null {
  if (typeof raw !== 'string') return 'required';
  const username = raw.trim();
  if (username.length === 0) return 'required';
  if (username.length < BALANCE.account.usernameMinLength) return 'too_short';
  if (username.length > BALANCE.account.usernameMaxLength) return 'too_long';
  if (!USERNAME_PATTERN.test(username)) return 'charset';
  // A leading or trailing underscore reads as whitespace at this font size.
  if (username.startsWith('_') || username.endsWith('_')) return 'edges';
  return null;
}

/** Case is preserved for display; uniqueness is case-insensitive in Postgres. */
export function normaliseUsername(raw: string): string {
  return raw.trim();
}

// --- guest names -------------------------------------------------------------

/**
 * The name a device is given the moment it chooses to play as a guest.
 *
 * Takes its randomness as an argument rather than reaching for `Math.random()`,
 * because this file is in `shared/` and everything here has to stay pure: the
 * client passes a CSPRNG-backed source, a test passes a sequence it chose. Same
 * discipline as `RuleContext.random`, for the same reason.
 *
 * The name is generated on the device, not on the server, and that is the whole
 * point rather than a detail: a first launch with no connection still has to
 * produce a player with a name, and the *same* name has to survive the trip to
 * the server once there is one. A server-generated name would mean the guest is
 * anonymous until the network says otherwise.
 */
export function createGuestName(random: () => number): string {
  const { guestNamePrefix, guestNameAlphabet, guestNameSuffixLength } = BALANCE.account;

  let suffix = '';
  for (let index = 0; index < guestNameSuffixLength; index += 1) {
    const pick = Math.floor(random() * guestNameAlphabet.length);
    // A source that returns exactly 1 (or drifts above it) would index past the
    // end and append `undefined`; clamping costs nothing and cannot surprise.
    suffix += guestNameAlphabet[Math.min(pick, guestNameAlphabet.length - 1)];
  }

  return `${guestNamePrefix}${suffix}`;
}

/** Does this look like a name we generated? Used to offer a rename, never to trust one. */
export function isGeneratedGuestName(name: unknown): boolean {
  if (typeof name !== 'string') return false;
  const { guestNamePrefix, guestNameAlphabet, guestNameSuffixLength } = BALANCE.account;
  if (!name.startsWith(guestNamePrefix)) return false;

  const suffix = name.slice(guestNamePrefix.length);
  return (
    suffix.length === guestNameSuffixLength &&
    [...suffix].every((character) => guestNameAlphabet.includes(character))
  );
}

// --- e-mail ------------------------------------------------------------------

export type EmailError = 'required' | 'too_long' | 'malformed';

/**
 * Intentionally permissive. The only test that proves an address exists is
 * sending mail to it, so this rejects what is obviously not an address and
 * leaves the rest to the (still unchosen) delivery provider. A stricter regexp
 * would mostly succeed at rejecting valid, unusual addresses.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

export function emailError(raw: unknown): EmailError | null {
  if (typeof raw !== 'string') return 'required';
  const email = raw.trim();
  if (email.length === 0) return 'required';
  if (email.length > BALANCE.account.emailMaxLength) return 'too_long';
  if (!EMAIL_PATTERN.test(email)) return 'malformed';
  return null;
}

export function normaliseEmail(raw: string): string {
  // Lower-cased on the way in: the unique index is on `lower(email)`, and an
  // address is not a display string, so nothing is lost by storing it folded.
  return raw.trim().toLowerCase();
}

// --- password strength (presentation only) -----------------------------------

/**
 * A 0–4 score for the register screen's four-segment meter. Length dominates
 * because it is the only property that reliably costs an attacker work; the
 * variety terms exist so the meter still moves for someone typing a short
 * complex password, and nothing here can reject a password — that is
 * `passwordPolicyError` on the server, which only measures length.
 */
export function passwordStrength(password: string): 0 | 1 | 2 | 3 | 4 {
  if (password.length === 0) return 0;

  const { passwordMinLength } = BALANCE.account;
  let score = 0;
  if (password.length >= passwordMinLength) score += 2;
  else if (password.length >= passwordMinLength - 4) score += 1;

  const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((re) =>
    re.test(password),
  ).length;
  if (classes >= 2) score += 1;
  if (classes >= 3 && password.length >= passwordMinLength + 4) score += 1;

  return Math.min(score, 4) as 0 | 1 | 2 | 3 | 4;
}

// --- wire shapes -------------------------------------------------------------

/** An account as the client is allowed to see it. Never carries the hash. */
export interface AccountSummary {
  id: string;
  /** The player row this account owns. Identical to the guest's id after conversion. */
  playerId: PlayerId;
  username: string;
  email: string;
  emailVerified: boolean;
  createdAt: Timestamp;
}

/**
 * The opaque session secret, handed over exactly once. Postgres keeps only its
 * digest, so a database read cannot recover a live session.
 */
export interface AuthSessionInfo {
  token: string;
  expiresAt: Timestamp;
  /** Echoed back so the client does not have to remember what it asked for. */
  rememberMe: boolean;
}

export interface AuthErrorBody {
  error: AuthErrorCode;
  /** Which input to mark, when the failure belongs to one. */
  field?: 'username' | 'email' | 'password' | 'identifier';
  /** Seconds until a rate-limited caller may try again. */
  retryAfter?: number;
}

/**
 * Stable codes rather than prose. The screens render Spanish, the server has no
 * business choosing their wording, and a client that switched on message text
 * would break the first time a sentence was rephrased.
 */
export type AuthErrorCode =
  | 'device_required'
  | 'invalid_username'
  | 'invalid_email'
  | 'invalid_password'
  | 'username_taken'
  | 'email_taken'
  | 'player_already_claimed'
  | 'invalid_credentials'
  | 'account_disabled'
  | 'rate_limited'
  | 'unauthorized';
